import { c as _c } from "react/compiler-runtime";
import { Button, Close, FilledHeart, Heart } from '@sprd/sprd-component-kit/v2';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { FavoriteItem, useAddFavoriteItem, useFavorites, useRemoveFavoriteItem } from '@/hooks/useFavorites';
import { MAX_FAVORITES } from '@/utility/constants';
import { createFavoriteId } from '@/utility/createFavoriteId';
import { trackFavoriteAddition, trackFavoriteRemoval } from '@/utility/tracking/trackFavorite';
export type FavoriteButtonItem = Omit<FavoriteItem, 'id' | 'locale' | 'dateCreated'> & {
  shopId: number;
  price: number;
  label: string | undefined;
  isDeleteVariant?: boolean;
};
type FavoriteButton = FavoriteButtonItem & {
  variant?: 'plain' | 'invert' | 'ghost';
  className?: string;
  iconClassName?: string;
  /** Marks the PDP's own button so e2e can tell it apart from the product cards' */
  testId?: string;
};
export const FavoriteButton = t0 => {
  const $ = _c(37);
  const {
    sellableId,
    compositionId,
    productTypeId,
    appearanceId,
    sizeId,
    shopId,
    price,
    label,
    variant: t1,
    isDeleteVariant,
    className,
    iconClassName,
    testId
  } = t0;
  const variant = t1 === undefined ? "plain" : t1;
  const t = useTranslations("favorites");
  const {
    data: favorites
  } = useFavorites();
  const addFavorite = useAddFavoriteItem();
  const removeFavorite = useRemoveFavoriteItem();
  let t2;
  if ($[0] !== appearanceId || $[1] !== compositionId || $[2] !== favorites || $[3] !== sellableId) {
    t2 = favorites?.some(favorite => favorite.sellableId === sellableId && favorite.compositionId === compositionId && favorite.appearanceId === appearanceId);
    $[0] = appearanceId;
    $[1] = compositionId;
    $[2] = favorites;
    $[3] = sellableId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isFavorite = !!t2;
  const isDisabled = !isFavorite && !!favorites && favorites.length >= MAX_FAVORITES;
  let t3;
  if ($[5] !== addFavorite || $[6] !== appearanceId || $[7] !== compositionId || $[8] !== isDisabled || $[9] !== isFavorite || $[10] !== label || $[11] !== price || $[12] !== productTypeId || $[13] !== removeFavorite || $[14] !== sellableId || $[15] !== shopId || $[16] !== sizeId) {
    t3 = () => {
      const favoritem = {
        compositionId,
        sellableId,
        productTypeId,
        appearanceId,
        sizeId
      };
      if (!isFavorite) {
        if (isDisabled) {
          return;
        }
        addFavorite.mutate(favoritem);
        trackFavoriteAddition({
          shopId,
          price,
          label,
          ...favoritem
        });
      } else {
        removeFavorite.mutate(createFavoriteId(compositionId, sellableId, appearanceId));
        trackFavoriteRemoval({
          shopId,
          price,
          label,
          ...favoritem
        });
      }
    };
    $[5] = addFavorite;
    $[6] = appearanceId;
    $[7] = compositionId;
    $[8] = isDisabled;
    $[9] = isFavorite;
    $[10] = label;
    $[11] = price;
    $[12] = productTypeId;
    $[13] = removeFavorite;
    $[14] = sellableId;
    $[15] = shopId;
    $[16] = sizeId;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const handleFavorite = t3;
  const HeartIcon = isFavorite ? FilledHeart : Heart;
  let t4;
  if ($[18] !== isFavorite || $[19] !== t) {
    t4 = isFavorite ? t("a11y.removeFrom") : t("a11y.addTo");
    $[18] = isFavorite;
    $[19] = t;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const ariaLabel = t4;
  const t5 = isDeleteVariant ? "invert" : variant;
  let t6;
  if ($[21] !== className || $[22] !== isDeleteVariant) {
    t6 = clsx({
      "group/delete hover:bg-white": isDeleteVariant
    }, className);
    $[21] = className;
    $[22] = isDeleteVariant;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = label ? `${ariaLabel} – ${label}` : ariaLabel;
  let t8;
  if ($[24] !== HeartIcon || $[25] !== iconClassName || $[26] !== isDeleteVariant) {
    t8 = isDeleteVariant ? <><HeartIcon className={clsx(iconClassName, "size-6 group-hover/delete:hidden")} aria-hidden={true} /><Close className={clsx(iconClassName, "hidden size-6 group-hover/delete:block")} aria-hidden={true} /></> : <HeartIcon className={clsx(iconClassName, "size-6")} aria-hidden={true} />;
    $[24] = HeartIcon;
    $[25] = iconClassName;
    $[26] = isDeleteVariant;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== handleFavorite || $[29] !== isDisabled || $[30] !== isFavorite || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8 || $[35] !== testId) {
    t9 = <Button variant={t5} className={t6} onClick={handleFavorite} aria-label={t7} aria-pressed={isFavorite} aria-disabled={isDisabled} data-testid={testId}>{t8}</Button>;
    $[28] = handleFavorite;
    $[29] = isDisabled;
    $[30] = isFavorite;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = testId;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};